import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, CircleCheck, Loader2 } from "lucide-react";
import {
  FOGBUGZ_TO_ADO_RUN_PHASE_LABELS,
  fogbugzToAdoProgressEventSchema,
  type FogbugzToAdoRunPhase,
  type FogbugzToAdoToolsetBridge,
} from "@ttc-spectree/toolbox-contract";
import { Button } from "@/components/ui/button";
import type { FogbugzToAdoEngineMessage } from "@/lib/toolbox/fogbugz-to-ado";
import { cn } from "@/lib/utils";

/**
 * Small pieces shared by the FogBugz to ADO steps (epic PI-SPEC-SW-E311,
 * feature PI-SPEC-SW-1640): a notice card, a waiting line, the Claude run
 * line with Cancel, and an engine refusal with its Setup link.
 */

export function Notice({
  tone,
  testId,
  children,
}: {
  tone: "error" | "ok" | "warn";
  testId: string;
  children: ReactNode;
}) {
  const Icon = tone === "ok" ? CircleCheck : CircleAlert;
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      data-testid={testId}
      className={cn(
        "flex items-start gap-2.5 rounded-[10px] border px-4 py-3 text-[13px] leading-relaxed",
        tone === "error" && "border-red-500/40 bg-red-500/5",
        tone === "ok" && "border-emerald-500/40 bg-emerald-500/5",
        tone === "warn" && "border-amber-500/40 bg-amber-500/5",
      )}
    >
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "error" && "text-red-400",
          tone === "ok" && "text-emerald-400",
          tone === "warn" && "text-amber-400",
        )}
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-2">{children}</div>
    </div>
  );
}

export function Waiting({ label, testId }: { label: string; testId?: string }) {
  return (
    <span className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid={testId}>
      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {label}
    </span>
  );
}

/** The plain line for a run phase and its optional activity. Pure; exported for tests. */
export function runLine(phase: FogbugzToAdoRunPhase, activity?: string): string {
  const label = FOGBUGZ_TO_ADO_RUN_PHASE_LABELS[phase];
  return activity && activity.trim() !== "" ? `${label}: ${activity.trim()}` : label;
}

/**
 * While Claude reads the case or drafts the Bug: the phase in plain words
 * (from `onProgress`, when the app offers it) and Cancel. Cancel asks the
 * app to stop; the run then resolves with CANCELED.
 */
export function ClaudeRun({
  fogbugz,
  caseId,
  phase,
  testId,
}: {
  fogbugz: FogbugzToAdoToolsetBridge;
  caseId: number;
  phase: FogbugzToAdoRunPhase;
  testId: string;
}) {
  const [line, setLine] = useState(() => runLine(phase));
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    setLine(runLine(phase));
    if (typeof fogbugz.onProgress !== "function") return undefined;
    return fogbugz.onProgress((event) => {
      const parsed = fogbugzToAdoProgressEventSchema.safeParse(event);
      if (!parsed.success || parsed.data.caseId !== caseId) return;
      setLine(runLine(parsed.data.phase, parsed.data.activity));
    });
  }, [fogbugz, caseId, phase]);

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testId}>
      <Waiting label={line} />
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={canceling}
        onClick={() => {
          setCanceling(true);
          void fogbugz.cancel().catch(() => undefined);
        }}
      >
        {canceling ? "Stopping…" : "Cancel"}
      </Button>
    </div>
  );
}

/** An engine refusal: the plain sentence, Setup checks when that is where it is fixed, and Try again. */
export function EngineNotice({
  message,
  testId,
  onRetry,
}: {
  message: FogbugzToAdoEngineMessage;
  testId: string;
  onRetry?: () => void;
}) {
  return (
    <Notice tone="error" testId={testId}>
      <p>{message.text}</p>
      <div className="flex flex-wrap items-center gap-2">
        {message.setup && (
          <Button asChild size="sm" variant="outline">
            <Link to="/toolbox/setup">Open Setup checks</Link>
          </Button>
        )}
        {onRetry && (
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        )}
      </div>
    </Notice>
  );
}

/** The message of a failed SpecTree call, or `fallback` when it carries none. Pure. */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message && !/^API Error: \d+$/.test(error.message) ? error.message : fallback;
}
